/**
 * Share-with-customer card. Foreman/PM/Admin can mint a time-limited URL
 * that lets the customer (no account) download the PDF closeout + raw
 * ZIP. Shows existing links with last-accessed and revoke control.
 *
 * Renders inline on the Closeout tab of the project detail page.
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link2, Copy, Check, Ban, Calendar, Eye, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function shareUrlFor(token) {
  return `${window.location.origin}/share/${token}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (_e) {
    return iso;
  }
}

export default function ShareLinkCard({ project }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expiresDays, setExpiresDays] = useState(30);
  const [copiedToken, setCopiedToken] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/share`);
      setLinks(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, [project.id]);

  const create = async () => {
    setCreating(true);
    try {
      await api.post(`/projects/${project.id}/share`, { expires_days: Number(expiresDays) || 30 });
      toast.success("Share link created — copy the URL and send it to the customer.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create link.");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this link? The customer will no longer be able to download.")) return;
    try {
      await api.post(`/share/${token}/revoke`);
      toast.success("Link revoked.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke.");
    }
  };

  const copy = async (token) => {
    const url = shareUrlFor(token);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopiedToken((t) => (t === token ? null : t)), 1800);
    } catch (_e) {
      toast.error("Couldn't access clipboard — long-press the field to copy.");
    }
  };

  const activeLinks = links.filter((l) => !l.revoked_at);

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="share-link-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-blue-400" />
          <div className="font-display font-bold tracking-tight">Share with Customer</div>
        </div>
        <div className="text-xs text-slate-400 -mt-2">
          Create a read-only link the customer can use to download the closeout PDF and raw ZIP — no Force2 login required. Link expires automatically; you can revoke at any time.
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-slate-400">Expires (days)</Label>
            <Input
              type="number"
              min={1}
              max={365}
              value={expiresDays}
              onChange={(e) => setExpiresDays(e.target.value)}
              className="mt-1 bg-slate-950 border-slate-700 w-28 font-mono"
              data-testid="share-expires-input"
            />
          </div>
          <Button
            onClick={create}
            disabled={creating}
            className="bg-blue-700 hover:bg-blue-600 font-display tracking-wider"
            data-testid="share-create-btn"
          >
            <Link2 className="w-4 h-4 mr-1.5" /> Create Link
          </Button>
        </div>

        {loading ? (
          <div className="text-xs text-slate-400">Loading links…</div>
        ) : activeLinks.length === 0 && links.length === 0 ? (
          <div className="text-xs text-slate-500 italic">No share links yet. Create one above.</div>
        ) : (
          <div className="space-y-2">
            {links.map((l) => {
              const url = shareUrlFor(l.token);
              const expired = (() => {
                try { return new Date(l.expires_at) < new Date(); } catch { return false; }
              })();
              const revoked = !!l.revoked_at;
              return (
                <div
                  key={l.token}
                  data-testid={`share-link-row-${l.token.slice(0, 8)}`}
                  className={`rounded-md border p-3 ${revoked || expired ? "bg-slate-950/60 border-slate-800 opacity-70" : "bg-slate-950 border-slate-700"}`}
                >
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={url}
                      className="bg-slate-900 border-slate-800 text-xs font-mono"
                      onFocus={(e) => e.target.select()}
                      data-testid={`share-url-${l.token.slice(0, 8)}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copy(l.token)}
                      disabled={revoked || expired}
                      className="border-slate-700 bg-slate-900 hover:bg-slate-800"
                      data-testid={`share-copy-${l.token.slice(0, 8)}`}
                    >
                      {copiedToken === l.token ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      asChild
                      disabled={revoked || expired}
                      className="border-slate-700 bg-slate-900 hover:bg-slate-800"
                    >
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-slate-400">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Expires {formatDate(l.expires_at)}</span>
                    <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Accessed {l.access_count || 0}× · last {formatDate(l.last_accessed_at)}</span>
                    {revoked && <span className="text-red-400">Revoked {formatDate(l.revoked_at)}</span>}
                    {expired && !revoked && <span className="text-amber-400">Expired</span>}
                    {!revoked && !expired && (
                      <button
                        type="button"
                        onClick={() => revoke(l.token)}
                        className="text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                        data-testid={`share-revoke-${l.token.slice(0, 8)}`}
                      >
                        <Ban className="w-3 h-3" /> Revoke
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
